import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CompanyTableModel } from 'src/app/models/tables/management/company-table.model';
import { UserService } from 'src/app/services/authentication/user.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-company',
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.scss'],
  standalone: false
})
export class CompanyComponent implements OnInit {

  ascendant: boolean = true;
  columnSort: string | null = null;
  data: PagedResponseModel<CompanyTableModel> = new PagedResponseModel<CompanyTableModel>();
  header: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  isASADMIN: boolean = false;
  isPrincipalCompany: boolean = false;
  limit: number = 10;
  nameWindow: string = 'Compañías';
  page: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  url: string = 'company';

  constructor(private companyService: CompanyService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private headerService: HeaderService,
    private userService: UserService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllCompaniesWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async getAllCompaniesWithPagination() {
    try {
      this.data = await firstValueFrom(this.companyService.getAllCompaniesWithPagination(this.page, this.limit,
        this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar las compañías.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.header.length === 0) {
        this.header = await firstValueFrom(this.companyService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general la compañía.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
    await this.getUserPermissions();
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getUserPermissions() {
    await Promise.all([
      this.userAuthenticatedIsASADMIN(),
      this.userAuthenticatedIsPrincipalCompany(),
    ]);

    if (this.isASADMIN) {
      this.permissionUpdatable = true;
    } else {
      await this.getPermissionProfileByUrl();
    }
  }

  async userAuthenticatedIsASADMIN() {
    try {
      const response = await firstValueFrom(this.userService.userAuthenticatedIsASADMIN());
      this.isASADMIN = response;
    } catch (error) {
      console.error('Error al verificar si el usuario es ASADMIN.', error);
    }
  }

  async userAuthenticatedIsPrincipalCompany() {
    try {
      const response = await firstValueFrom(this.userService.userAuthenticatedIsPrincipalCompany());
      this.isPrincipalCompany = response;
    } catch (error) {
      console.error('Error al validar si la compañía es principal.', error);
    }
  }

  newRegister() {
    this.router.navigate([`/${this.url}/register`]);
  }

  async onAction(event: { action: string, item?: CompanyTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.id}`]);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllCompaniesWithPagination();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
